import { useEffect, useState } from "react";
import { NotificationTypes } from "src/enums/Notifications";
import { UserRole } from "src/enums/User";
import { UserManagement } from "src/interfaces";
import {
  updateAdminSettings,
  updateResolveComplaint,
} from "src/redux/redux-store";
import { useAppDispatch, useAppSelector } from "src/redux/redux-store/hooks";
import { ResponseKind } from "src/services/services/MainService";
import { adminService } from "src/services/services/admin/admin.api";
import { authService } from "src/services/services/auth/auth.api";
import {
  sendFailureNotification,
  sendSuccessNotification,
} from "src/utils/notifications";

const useAdminSettings = () => {
  const [users, setUsers] = useState<UserManagement[]>([]);
  const [feedback, setFeedback] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const adminSettings = useAppSelector((state) => state.app.adminConfig);
  const selectedComplaints = useAppSelector(
    (state) => state.app.selectedComplaint
  );
  const dispatch = useAppDispatch();

  const getSettings = async () => {
    const admin_config = await adminService.getConfig();

    if (admin_config.kind == ResponseKind.OK) {
      if (admin_config?.data) {
        let obj = { promo_code: "", promo_code_id: "" };
        let promoCode = admin_config?.data?.default_promo_code;
        if (promoCode) {
          const promoCodeObj = JSON.parse(promoCode);
          obj.promo_code = promoCodeObj.promo_code;
          obj.promo_code_id = promoCodeObj.promo_code_id;
        }
        const values = {
          ...admin_config?.data,
          promo_code: obj,
        };
        dispatch(updateAdminSettings(values));
      }
    }
  };

  const resolveComplaint = async () => {
    dispatch(updateResolveComplaint());
  };

  const enableGDrive = async (value: boolean) => {
    try {
      setLoading(true);
      const response = await adminService.updateConfig({
        gdrive_enabled: value,
      });
      if (response.kind == ResponseKind.OK) {
        setError(false);
        setFeedback("GDrive link was successfully updated");
        setTimeout(() => setFeedback(""), 3000);
      } else {
        throw "Error";
      }
    } catch (e) {
      setError(true);
      setFeedback("GDrive link couldn't be updated");
      setTimeout(() => setFeedback(""), 3000);
    } finally {
      getSettings();
      setLoading(false);
    }
  };

  const enableMaintenanceNotice = async (value: boolean) => {
    try {
      setLoading(true);
      const response = await adminService.updateConfig({
        gdrive_enabled: value,
      });
      if (response.kind == ResponseKind.OK) {
        setError(false);
        setFeedback("GDrive link was successfully updated");
        setTimeout(() => setFeedback(""), 3000);
      } else {
        throw "Error";
      }
    } catch (e) {
      setError(true);
      setFeedback("GDrive link couldn't be updated");
      setTimeout(() => setFeedback(""), 3000);
    } finally {
      getSettings();
      setLoading(false);
    }
  };

  const updateDisclaimer = async (
    childValue: string,
    parentValue: string,
    cb: () => void
  ) => {
    try {
      setLoading(true);
      const response = await adminService.updateConfig({
        parent_disclaimers: parentValue,
        child_disclaimers: childValue,
      });
      if (response.kind == ResponseKind.OK) {
        setError(false);
        setFeedback("Disclaimer was successfully updated");
        cb();
        setTimeout(() => setFeedback(""), 3000);
      } else {
        throw "Error";
      }
    } catch (e) {
      setError(true);
      setFeedback("Disclaimer couldn't be updated");
      setTimeout(() => setFeedback(""), 3000);
    } finally {
      getSettings();
      setLoading(false);
    }
  };

  const updateGreetingsMessage = async (value: string) => {
    try {
      setLoading(true);
      const response = await adminService.updateConfig({
        greeting_message: value,
      });
      if (response.kind == ResponseKind.OK) {
        setError(false);
        setFeedback("Greetings Message was successfully updated");
        setTimeout(() => setFeedback(""), 3000);
      } else {
        throw "Error";
      }
    } catch (e) {
      setError(true);
      setFeedback("Greetings Message couldn't be updated");
      setTimeout(() => setFeedback(""), 3000);
    } finally {
      getSettings();
      setLoading(false);
    }
  };

  const updateLLMConfig = async (values: any) => {
    try {
      setLoading(true);
      const response = await adminService.updateConfig(values);
      if (response.kind == ResponseKind.OK) {
        setError(false);
        setFeedback("LLM Config was successfully updated");
        setTimeout(() => setFeedback(""), 3000);
      } else {
        throw "Error";
      }
    } catch (e) {
      setError(true);
      setFeedback("LLM Config couldn't be updated");
      setTimeout(() => setFeedback(""), 3000);
    } finally {
      getSettings();
      setLoading(false);
    }
  };

  const getUsers = async () => {
    try {
      setLoading(true);
      const response = await adminService.getUsers();
      if (response.kind == ResponseKind.OK && response.data) {
        let tempUsers = response.data.users;

        tempUsers = tempUsers.filter((item) => item.role != UserRole.admin);

        setUsers(tempUsers);
      } else {
        throw "Error";
      }
    } catch (e) {
      sendFailureNotification(
        NotificationTypes.UserManagement,
        "Users could not be fetched"
      );
    } finally {
      setLoading(false);
    }
  };

  const changeUserStatus = async (user: UserManagement) => {
    try {
      setLoading(true);
      const response = await adminService.changeUserStatus(user.id);
      if (response.kind == ResponseKind.OK && response.data) {
        sendSuccessNotification(
          NotificationTypes.UserManagement,
          "User status changed successfully"
        );
      } else {
        throw "Error";
      }
    } catch (e) {
      sendFailureNotification(
        NotificationTypes.UserManagement,
        "Users status could not be modified"
      );
    } finally {
      setLoading(false);
      getUsers();
    }
  };

  const promoteUserLevel = async (user: UserManagement) => {
    const username = user.username;
    if (!username) return false;
    try {
      setLoading(true);
      const response = await adminService.promoteUserLevel(username);
      if (response.kind === ResponseKind.OK) {
        sendSuccessNotification(
          NotificationTypes.UserManagement,
          "User level promoted"
        );
        await getUsers();
        return true;
      } else {
        const errorMessage =
          "errors" in response
            ? typeof response.errors === "string"
              ? response.errors
              : response.errors?.data?.message || "Could not promote user level"
            : "Could not promote user level";
        throw new Error(errorMessage);
      }
    } catch (e) {
      console.log("EERRR", e);
      const errorMsg =
        e instanceof Error ? e.message : "Could not promote user level";
      sendFailureNotification(NotificationTypes.UserManagement, errorMsg);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateUserSubjects = async (
    user: UserManagement,
    subjectIds: number[]
  ) => {
    if (!user.username || !subjectIds.length) return;
    try {
      setLoading(true);
      const response = await adminService.updateUserSubjects(
        user.username || "",
        subjectIds
      );
      if (response.kind === ResponseKind.OK) {
        sendSuccessNotification(
          NotificationTypes.UserManagement,
          "Subjects updated successfully"
        );
        await getUsers();
        return true;
      } else {
        throw new Error("Failed to update subjects");
      }
    } catch (e) {
      const errorMsg =
        e instanceof Error ? e.message : "Could not update subjects";
      sendFailureNotification(NotificationTypes.UserManagement, errorMsg);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteUserAccount = async (user: UserManagement) => {
    try {
      setLoading(true);
      const response = await adminService.deleteUserAccount(user.id);
      if (response.kind === ResponseKind.OK) {
        sendSuccessNotification(
          NotificationTypes.UserManagement,
          "User deleted"
        );
      } else {
        throw "Error";
      }
    } catch (e) {
      sendFailureNotification(
        NotificationTypes.UserManagement,
        "User could not be deleted"
      );
    } finally {
      setLoading(false);
      getUsers();
    }
  };

  const createParent = async (
    name: string,
    email: string,
    password: string,
    phone?: string
  ) => {
    try {
      setLoading(true);
      const response = await adminService.registerParent(
        name,
        email,
        password,
        phone || ""
      );
      if (response.kind === ResponseKind.OK) {
        sendSuccessNotification(
          NotificationTypes.UserManagement,
          "Parent created"
        );
      } else {
        throw "Error";
      }
    } catch (e) {
      sendFailureNotification(
        NotificationTypes.UserManagement,
        "Could not create parent"
      );
    } finally {
      setLoading(false);
      getUsers();
    }
  };

  const createLearner = async (
    name: string,
    username: string,
    password: string,
    level: number,
    subjectIds: number[],
    school: string,
    dateOfBirth: string,
    parentEmail: string,
    avatarPath?: string
  ) => {
    try {
      setLoading(true);

      const formData = new FormData();

      // Add profile picture if selected
      if (avatarPath) {
        const response = await fetch(avatarPath);
        const blob = await response.blob();
        formData.append("profile_picture", blob, avatarPath.split("/").pop());
      }

      // Add child_data as JSON string
      const childData = {
        name,
        level,
        subjects: subjectIds,
        username,
        password,
        school,
        date_of_birth: dateOfBirth,
      };
      formData.append("child_data", JSON.stringify(childData));

      const response = await adminService.registerChild(formData, parentEmail);
      if (response.kind === ResponseKind.OK) {
        sendSuccessNotification(
          NotificationTypes.UserManagement,
          "Learner created"
        );
      } else {
        const errorMessage =
          "errors" in response
            ? typeof response.errors === "string"
              ? response.errors
              : response.errors?.data?.message || "Could not create learner"
            : "Could not create learner";
        throw new Error(errorMessage);
      }
    } catch (e) {
      const errorMsg =
        e instanceof Error ? e.message : "Could not create learner";
      sendFailureNotification(NotificationTypes.UserManagement, errorMsg);
      throw e;
    } finally {
      setLoading(false);
      getUsers();
    }
  };
  const updateUserPassword = async (user: UserManagement, password: string) => {
    try {
      setLoading(true);
      const response = await adminService.updateUserPassword(user.id, password);
      if (response.kind === ResponseKind.OK) {
        sendSuccessNotification(
          NotificationTypes.UserManagement,
          "Password updated"
        );
      } else {
        throw "Error";
      }
    } catch (e) {
      sendFailureNotification(
        NotificationTypes.UserManagement,
        "Could not update password"
      );
    } finally {
      setLoading(false);
      getUsers();
    }
  };

  const updatePaymentConfig = async (
    promoCode: string,
    promoCodeId: string
  ) => {
    try {
      setLoading(true);

      const defaultPromoCode = {
        promo_code: promoCode,
        promo_code_id: promoCodeId,
      };
      const JSON_VALUES = JSON.stringify(defaultPromoCode);
      const values = {
        default_promo_code: JSON_VALUES,
      };

      const response = await adminService.updateConfig(values);
      if (response.kind == ResponseKind.OK) {
        setError(false);
        setFeedback("Payment Config was successfully updated");
        setTimeout(() => setFeedback(""), 3000);
      } else {
        throw "Error";
      }
    } catch (e) {
      setError(true);
      setFeedback("Payment Config couldn't be updated");
      setTimeout(() => setFeedback(""), 3000);
    } finally {
      getSettings();
      setLoading(false);
    }
  };

  useEffect(() => {
    getSettings();
  }, []);

  return {
    feedback: feedback,
    getSettings: getSettings,
    updateGreetingsMessage,
    error: error,
    getUsers,
    changeUserStatus,
    promoteUserLevel,
    updateUserSubjects,
    deleteUserAccount,
    users,
    updateLLMConfig,
    selectedComplaints: selectedComplaints,
    loading: loading,
    updatePaymentConfig,
    resolveComplaint: resolveComplaint,
    updateDisclaimer: updateDisclaimer,
    enableGDrive: enableGDrive,
    adminSettings: adminSettings,
    updateUserPassword,
    createParent,
    createLearner,
  };
};

export default useAdminSettings;
